import NextHead from 'next/head';
import { useMemo } from 'react';

interface Props {
  title?: string;
  description?: string;
  openGraph?: {
    title?: string;
    description?: string;
    image?: string | string[];
  };
}

// Some tags are not shown client side and dont have to be translated
export const Head = ({ title, description, openGraph }: Props) => {
  const pageTitle = useMemo(
    () =>
      title ??
      'WeTransfer - Send Large Files & Share Photos Online - Up to 2GB Free',
    [title]
  );

  const pageDescription = useMemo(
    () =>
      description ??
      'WeTransfer is the simplest way to send your files around the world. Share large files and photos. Transfer up to 2GB free. File sharing made easy!',
    [description]
  );

  const og = useMemo(
    () => ({
      title:
        openGraph?.title ??
        'WeTransfer - Send Large Files & Share Photos Online - Up to 2GB Free',
      description:
        openGraph?.description ??
        'WeTransfer is the simplest way to send your files around the world',
      image: openGraph?.image ?? '/wt-facebook.png',
    }),
    [openGraph]
  );

  return (
    <NextHead>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        name="viewport"
        content="width=device-width, minimum-scale=1, initial-scale=1"
      />

      <meta name="pinterest" content="nopin" />

      <meta name="referrer" content="origin" />

      <meta property="og:title" content={og.title} />
      <meta property="og:description" content={og.description} />
      {typeof og.image === 'string' ? (
        <meta property="og:image" content={og.image} />
      ) : (
        og.image.map((image) => (
          <meta property="og:image" content={image} key={image} />
        ))
      )}

      <meta property="og:type" content="website" />
      <meta property="fb:app_id" content="265125293564341" />

      <title dir="ltr" data-testid="page-title">
        {pageTitle}
      </title>
      <meta name="description" content={pageDescription} />
      <meta name="author" content="WeTransfer" />
      <meta name="application-name" content="WeTransfer" />

      <link rel="shortcut icon" href="/favicon.ico" />
      <link rel="icon" sizes="16x16 32x32" href="/favicon.ico" />
      <link rel="mask-icon" href="/favicon.svg" color="#17181A" />
      <link rel="icon" href="/favicon.ico" />

      <link rel="apple-touch-icon-precomposed" href="/apple-touch-icon.png" />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="152x152"
        href="/apple-touch-icon-152x152.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="167x167"
        href="/apple-touch-icon-167x167.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="180x180"
        href="/apple-touch-icon-180x180.png"
      />
    </NextHead>
  );
};
